import { forwardRef, useState } from 'react';
import type { InputHTMLAttributes } from 'react';
import { formatZarCents, parseZarAmount } from '@claripick/shared';
import { cx } from './cx';
import { controlBase } from './Input';
import { useFieldControl } from './Field';

export interface MoneyInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  /** The typed text, held by the caller exactly as before. */
  value: string;
  onChange: (value: string) => void;
  /** Re-render the accepted amount as `R 250 000,50` on blur. Default true. */
  formatOnBlur?: boolean;
}

/**
 * The one control for a Rand amount.
 *
 * Every money field in this product used to be
 * `<Input numeric type="number" step="0.01">` read back as
 * `Math.round(Number(text) * 100)`. South Africa writes a space for
 * thousands and a comma for decimals, and the product itself prints
 * `R 250 000,50` on every screen. Type that back into an
 * `input[type=number]` and the browser drops the comma and concatenates
 * the digits: `.value` comes back `"25000050"` with
 * `validity.badInput === false`, so nothing catches it, and the amount
 * stored is **R 25 000 050,00** — a hundredfold overstatement, silently.
 * Verified in Chromium under en-ZA, af-ZA and en-US; the locale makes no
 * difference, because `input[type=number]` only ever speaks the
 * period-decimal machine format.
 *
 * That number becomes the cash figure on a section 18A receipt handed to
 * a donor and the amount column of the IT3(d) file that goes to SARS, so
 * it is not a typo the user gets to notice later.
 *
 * `parseZarAmount` was written to prevent exactly this and its header
 * says so; it was wired to the CSV importer and to nothing a human
 * types. This control wires it to the keyboard:
 *
 *   - `type="text" inputMode="decimal"` — a phone still gets a numeric
 *     keypad, but a comma and a space survive the keystroke.
 *   - the parser decides which separator is the decimal one, per value,
 *     so `250 000,50`, `250,000.50` and `250000.50` all mean the same
 *     amount and `1,234` still means one thousand two hundred and
 *     thirty-four.
 *   - on blur the accepted amount is written back as `R 250 000,50`, so
 *     the figure on screen is the figure that will be stored. There is
 *     no way to be a hundred times out and not see it.
 *   - text that is not an amount is marked invalid rather than quietly
 *     read as zero.
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  { className, value, onChange, onBlur, formatOnBlur = true, id, ...rest },
  ref,
) {
  const field = useFieldControl(id);
  const [touched, setTouched] = useState(false);
  const invalid = touched && value.trim() !== '' && parseZarAmount(value) === null;

  return (
    <input
      ref={ref}
      {...field}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={value}
      aria-invalid={invalid || field['aria-invalid'] ? true : undefined}
      onChange={(e) => onChange(e.target.value)}
      onBlur={(e) => {
        setTouched(true);
        if (formatOnBlur) {
          const cents = parseZarAmount(e.target.value);
          if (cents !== null) onChange(formatZarCents(cents));
        }
        onBlur?.(e);
      }}
      className={cx(controlBase, 'record text-right', className)}
      {...rest}
    />
  );
});

/**
 * Cents from what a human typed, or null. The one conversion; call sites
 * must not do `Math.round(Number(text) * 100)` again.
 */
export function moneyCents(text: string): number | null {
  return parseZarAmount(text);
}

/** Cents, or 0 for blank or unparseable text. For fields that default to zero. */
export function moneyCentsOrZero(text: string): number {
  return parseZarAmount(text) ?? 0;
}
